import PropTypes from 'prop-types';
import React, { Component } from 'react';
import uuid from 'uuid';

import { CAROUSEL_PAGINATION_LIMIT, PAGINATION_TYPES } from './metadata';
import VideoItem from './video-item';
import VideoItemCarousel from './video-item-carousel';
import VideoPlayer from './video-player';
import PagerWidget from 'widgets/pager-widget/pager-widget';
import { Swiper, SwiperSlide, Navigation } from 'swiper/react';
import deepEqual from 'deep-equal';

import styles from './video-list.scss';
import 'swiper/swiper-bundle.css';
import arrow_left from './media/arrow_left.svg';
import arrow_right from './media/arrow_right.svg';

class VideoList extends Component {
    static propTypes = {
        API: PropTypes.object,
        columnSize: PropTypes.number.isRequired,
        componentId: PropTypes.string.isRequired,
        videos: PropTypes.array.isRequired,
        handleCTAChange: PropTypes.func,
        pageCount: PropTypes.number.isRequired,
        CallToAction: PropTypes.func.isRequired,
        videoClick: PropTypes.func.isRequired,
        videoClose: PropTypes.func.isRequired,
        currentVideo: PropTypes.object,
        VideoListTitle: PropTypes.func.isRequired,
        handleChangeTitle: PropTypes.func,
        showVideo: PropTypes.bool.isRequired,
        playerClass: PropTypes.string.isRequired,
        setCurrentPage: PropTypes.func.isRequired,
        currentPage: PropTypes.number.isRequired,
        isLoadMoreEnabled: PropTypes.bool.isRequired,
        settings: PropTypes.shape({
            selectedSeasonFilter: PropTypes.object,
            selectedShows: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    title: PropTypes.string.isRequired,
                    links: PropTypes.array
                })
            ),
            seasonFilter: PropTypes.bool,
            itemsPerPage: PropTypes.number.isRequired,
            displayVideoTitle: PropTypes.bool.isRequired,
            displayVideoListTitle: PropTypes.bool,
            displayShowTitle: PropTypes.bool.isRequired,
            displayVideoDescription: PropTypes.bool.isRequired,
            displayVideoMeta: PropTypes.bool.isRequired,
            includePassportVideos: PropTypes.bool.isRequired,
            videoType: PropTypes.string.isRequired,
            paginationType: PropTypes.string.isRequired,
            order: PropTypes.shape({
                value: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired
            }),
            player: PropTypes.shape({
                autoplay: PropTypes.bool.isRequired,
                topbar: PropTypes.bool.isRequired
            })
        }),
        data: PropTypes.shape({
            cta: PropTypes.object.isRequired,
            title: PropTypes.string.isRequired,
            titlePlaceholder: PropTypes.string.isRequired
        }),
        currentSite: PropTypes.object,
        site: PropTypes.object,
        callsign: PropTypes.string,
        changeComponentData: PropTypes.func,
        handleSelectSeasonFilter: PropTypes.func,
        selectedSeasonFilter: PropTypes.object,
        getVideos: PropTypes.func,
        getVideosCallback: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.loadMoreClick = this.loadMoreClick.bind(this);
        this.goToNext = this.goToNext.bind(this);
        this.goToPrevious = this.goToPrevious.bind(this);
        this.mainVideo = React.createRef();
        this.swiperRef = React.createRef();
        this.swiperInstance = null;
        this.nextPage = 1;
        this.state = {
            showArrowNext: '',
            showArrowPrev: 'hidden',
            loadPage: 1,
            loadMoreVideos: true,
            swiperKey: 0
        };
    }

    componentDidUpdate(prevProps) {
        const { currentPage, setCurrentPage, videos, settings, selectedSeasonFilter } = this.props;
        const { loadPage } = this.state;
        const nextPage = currentPage + 1;

        if (
            (!deepEqual(settings, prevProps.settings) ||
                !deepEqual(selectedSeasonFilter, prevProps.selectedSeasonFilter)) &&
            this.swiperInstance &&
            settings.paginationType === PAGINATION_TYPES.CAROUSEL
        ) {
            setCurrentPage(1);
            this.setState((prevState) => ({
                swiperKey: prevState.swiperKey + 1, // Increment key to force re-mount
                showArrowPrev: 'hidden',
                showArrowNext: '',
                loadPage: 1
            }));
        } else {
            if (loadPage > currentPage && settings.paginationType === PAGINATION_TYPES.CAROUSEL) {
                setCurrentPage(nextPage);
            }
        }

        if (
            !deepEqual(videos, prevProps.videos) &&
            settings.paginationType !== PAGINATION_TYPES.CAROUSEL
        ) {
            // reset load page to prevent spam when resizing from bigger screen rez to mobile rez
            this.setState({
                loadPage: 1
            });
        }
    }

    static defaultProps = {
        handleChangeTitle: () => {},
        handleCTAChange: () => {}
    };

    loadMoreClick(e) {
        const { setCurrentPage, currentPage } = this.props;
        const nextPage = currentPage + 1;
        e.preventDefault();
        e.stopPropagation();
        setCurrentPage(nextPage);
    }

    goToNext = (e) => {
        e.stopPropagation();
        const { setCurrentPage, currentPage } = this.props;
        setCurrentPage(currentPage + 1);
    };

    goToPrevious = (e) => {
        e.stopPropagation();
        const { setCurrentPage, currentPage } = this.props;
        setCurrentPage(currentPage - 1);
    };

    handlePageChanged = (page) => {
        const { setCurrentPage } = this.props;
        const oneBasedPage = page.selected + 1;
        setCurrentPage(oneBasedPage);
        this.mainVideo.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'center'
        });
    };

    handleVideoClick = (e, video) => {
        const { videoClick } = this.props;
        videoClick(e, video);
        //scroll to main video
        this.mainVideo.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        });
    };

    handleArrowClick = (instance) => {
        const { pageCount, currentPage } = this.props;
        let showArrowNext = '';
        let showArrowPrev = '';
        let loadPage = currentPage;

        if (
            instance.slides.length - instance.activeIndex < CAROUSEL_PAGINATION_LIMIT &&
            pageCount > currentPage
        ) {
            loadPage = currentPage + 1;
        }

        if (
            (instance.isEnd || instance.slides.length - instance.activeIndex === 2) &&
            pageCount === currentPage
        ) {
            showArrowNext = 'hidden';
        }
        if (instance.isBeginning) {
            showArrowPrev = 'hidden';
        }
        this.setState({
            showArrowPrev: showArrowPrev,
            showArrowNext: showArrowNext,
            loadPage: loadPage
        });
    };

    render() {
        const {
            settings,
            settings: { displayVideoListTitle = true, selectedShows },
            data,
            currentVideo,
            VideoListTitle,
            CallToAction,
            handleCTAChange,
            componentId,
            columnSize,
            handleChangeTitle,
            showVideo,
            playerClass,
            callsign,
            videoClose,
            currentPage,
            isLoadMoreEnabled,
            currentSite,
            handleSelectSeasonFilter,
            selectedSeasonFilter,
            videos
        } = this.props;

        const { showArrowPrev, showArrowNext, swiperKey } = this.state;

        const pageCount = this.state?.pageCount ? this.state.pageCount : this.props.pageCount;

        const displayPagination =
            settings.paginationType === PAGINATION_TYPES.PAGINATION && pageCount > 1;
        const displayLoadMore =
            settings.paginationType === PAGINATION_TYPES.LOAD_MORE && currentPage < pageCount;
        const currentVideoId = currentVideo && currentVideo.id;
        const currentShowId = selectedShows && selectedShows.length && selectedShows[0].id;
        const zeroBasedCurrentPage = Math.min(Math.max(0, currentPage - 1), pageCount);
        const pillButtonComponent = currentSite?.isPillButtonAvailable
            ? 'pill-button-component'
            : '';
        let cta = data?.cta[currentVideoId];
        let showCta = data?.cta[currentShowId];

        const displayCarousel = settings.paginationType === PAGINATION_TYPES.CAROUSEL;

        return (
            <div className={`video-list ${styles.videoList} column-${columnSize}`}>
                {displayVideoListTitle && (
                    <VideoListTitle
                        {...this.props}
                        id={componentId}
                        handleChangeTitle={handleChangeTitle}
                    />
                )}
                {!!settings.selectedShows.length && (
                    <VideoPlayer
                        {...this.props}
                        API={this.props.API}
                        settings={settings}
                        playerSettings={settings.player}
                        currentVideo={currentVideo}
                        cta={cta}
                        showCta={showCta}
                        showId={currentShowId}
                        handleCTAChange={handleCTAChange}
                        componentId={componentId}
                        CallToAction={CallToAction}
                        showVideo={showVideo}
                        playerClass={playerClass}
                        videoClose={videoClose}
                        reference={this.mainVideo}
                        callsign={callsign}
                        isPillButtonAvailable={currentSite?.isPillButtonAvailable}
                        handleSelectSeasonFilter={handleSelectSeasonFilter}
                        selectedSeasonFilter={selectedSeasonFilter}
                    />
                )}

                {displayCarousel && (
                    <Swiper
                        key={swiperKey}
                        className={showArrowPrev === 'hidden' ? 'is-first-page' : ''}
                        onBeforeInit={(swiper) => {
                            this.swiperRef.current = swiper;
                        }}
                        onSwiper={(swiper) => {
                            this.swiperInstance = swiper;
                        }}
                        onActiveIndexChange={(instance) => {
                            this.handleArrowClick(instance);
                        }}
                        slidesPerView={1.5}
                        slidesPerGroup={1}
                        spaceBetween={10}
                        breakpoints={{
                            768: {
                                slidesPerView: 2.5,
                                slidesPerGroup: 2,
                                spaceBetween: 5
                            },
                            1024: {
                                slidesPerView: 4.6,
                                slidesPerGroup: 4,
                                spaceBetween: 1
                            },
                            1280: {
                                slidesPerView: 4.6,
                                slidesPerGroup: 4,
                                spaceBetween: 10
                            }
                        }}
                        watchSlidesVisibility
                        watchSlidesProgress
                        modules={[Navigation]}
                        effect="fade">
                        {videos.map((video) => {
                            const key = video.id || uuid.v4();
                            const isActive = currentVideo && currentVideo.id === video.id;
                            if (!video) {
                                return null;
                            }
                            return (
                                <SwiperSlide key={key}>
                                    <VideoItemCarousel
                                        {...video}
                                        key={key}
                                        id={key}
                                        settings={settings}
                                        isActive={isActive}
                                        handleClick={(e) => this.handleVideoClick(e, video)}
                                        handleClose={this.handleClose}
                                        componentId={componentId}
                                    />
                                </SwiperSlide>
                            );
                        })}
                        <div className="splide__arrows">
                            <div
                                className={`${showArrowPrev} splide__arrow splide__arrow--prev`}
                                onClick={() => this.swiperRef?.current?.slidePrev()}>
                                <img src={arrow_left} alt="scroll left" />
                            </div>
                            <div
                                className={`${showArrowNext} splide__arrow splide__arrow--next`}
                                onClick={() => this.swiperRef?.current?.slideNext()}>
                                <img src={arrow_right} alt="scroll right" />
                            </div>
                        </div>
                    </Swiper>
                )}

                {!displayCarousel && (
                    <ul className="videos">
                        {videos.map((video) => {
                            const key = video.id || uuid.v4();
                            const isActive = currentVideo && currentVideo.id === video.id;
                            if (!video) {
                                return null;
                            }
                            return (
                                <VideoItem
                                    {...video}
                                    key={key}
                                    id={key}
                                    settings={settings}
                                    isActive={isActive}
                                    handleClick={(e) => this.handleVideoClick(e, video)}
                                    handleClose={this.handleClose}
                                    componentId={componentId}
                                />
                            );
                        })}
                    </ul>
                )}

                {displayPagination && (
                    <PagerWidget
                        pageCount={pageCount}
                        currentPage={zeroBasedCurrentPage}
                        goToNext={this.goToNext}
                        goToPrevious={this.goToPrevious}
                        handlePageChanged={this.handlePageChanged}
                    />
                )}

                {displayLoadMore && (
                    <div className="load-more-wrapper">
                        <a
                            href="#"
                            onClick={this.loadMoreClick}
                            className={`load-more ${
                                !isLoadMoreEnabled ? 'disabled' : ''
                            } ${pillButtonComponent}`}>
                            Load more
                        </a>
                    </div>
                )}
            </div>
        );
    }
}

export default VideoList;
